import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LeadCompetitionData, LeadAthlete, LeadGroup, LeadClass, LeadScore, RouteId,
  generateId, getScore, setScore, mergeScore,
  formatLeadScore, formatRank, formatCombined,
  computeAllGroupQualRankings, computeFinalsRankings, computeFinalsRankingsByGroup,
} from '../utils/leadCompetition';
import { loadLeadData, saveLeadData, saveScoreEntry } from '../firebase/leadService';
import { updateCompetition, TimetableRow } from '../firebase/competitionService';
import { useCompetition, AdminAccessGate } from '../context/CompetitionContext';

// ─── ScoreCell ────────────────────────────────────────────────────────────────

interface ScoreCellProps {
  score: LeadScore | undefined;
  onChange: (patch: Partial<Omit<LeadScore, 'athleteId' | 'routeId'>>) => void;
}

const ScoreCell: React.FC<ScoreCellProps> = ({ score, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localHolds, setLocalHolds] = useState<string>(
    score?.holds != null ? String(score.holds) : '',
  );

  const top = score?.top ?? false;
  const plus = score?.plus ?? false;
  const dnf = score?.dnf ?? false;
  const hasScore = top || dnf || score?.holds != null;

  // Sync from Firestore when the input is not focused
  useEffect(() => {
    if (inputRef.current !== document.activeElement) {
      setLocalHolds(score?.holds != null ? String(score.holds) : '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score?.holds]);

  // Also sync when top/dnf toggles clear the holds
  useEffect(() => {
    if (top || dnf) setLocalHolds('');
  }, [top, dnf]);

  const commitHolds = () => {
    if (localHolds.trim() === '') {
      onChange({ holds: null, top: false, dnf: false });
      return;
    }
    const v = parseInt(localHolds, 10);
    if (!isNaN(v) && v > 0) {
      onChange({ holds: v, top: false, dnf: false });
    }
  };

  return (
    <div className={`flex items-center gap-1.5 py-1 px-1 rounded ${hasScore ? 'bg-blue-50' : ''}`}>
      {/* Holds number */}
      <input
        ref={inputRef}
        type="number"
        min={1}
        max={100}
        value={localHolds}
        disabled={top || dnf}
        onChange={e => setLocalHolds(e.target.value)}
        onBlur={commitHolds}
        onKeyDown={e => { if (e.key === 'Enter') { commitHolds(); inputRef.current?.blur(); } }}
        placeholder="—"
        className="w-14 text-center border border-gray-300 rounded text-sm px-1 py-0.5 font-mono focus:outline-none focus:ring-2 focus:ring-secondary disabled:bg-gray-100 disabled:text-gray-400"
      />
      {/* Plus hold */}
      <button
        type="button"
        disabled={top || dnf}
        onClick={() => onChange({ plus: !plus, top: false, dnf: false })}
        className={`px-1.5 py-0.5 rounded text-xs font-bold border transition-colors ${
          plus && !top && !dnf
            ? 'bg-blue-500 text-white border-blue-500'
            : 'bg-white text-gray-400 border-gray-300 hover:border-blue-400 hover:text-blue-500'
        } disabled:opacity-30 disabled:cursor-not-allowed`}
      >
        Plus
      </button>
      {/* TOP toggle */}
      <button
        type="button"
        onClick={() => onChange({ top: !top, plus: false, dnf: false, holds: !top ? null : score?.holds ?? null })}
        className={`px-1.5 py-0.5 rounded text-xs font-bold border transition-colors ${
          top ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-400 border-gray-300 hover:border-green-500 hover:text-green-600'
        }`}
      >
        TOP
      </button>
      {/* DNF toggle */}
      <button
        type="button"
        onClick={() => onChange({ dnf: !dnf, top: false, plus: false, holds: !dnf ? null : score?.holds ?? null })}
        className={`px-1.5 py-0.5 rounded text-xs font-bold border transition-colors ${
          dnf ? 'bg-red-500 text-white border-red-500'
              : 'bg-white text-gray-400 border-gray-300 hover:border-red-400 hover:text-red-500'
        }`}
      >
        DNF
      </button>
      {/* Formatted score */}
      <span className={`text-xs font-bold w-8 text-right ${
        top ? 'text-green-700' : dnf ? 'text-red-500' : hasScore ? 'text-blue-700' : 'text-gray-300'
      }`}>
        {formatLeadScore(score)}
      </span>
    </div>
  );
};

// ─── AthleteBadge ─────────────────────────────────────────────────────────────

const AthleteBadge: React.FC<{ athlete: LeadAthlete }> = ({ athlete }) => (
  <span className={`inline-block text-[10px] font-bold px-1 py-0.5 rounded ml-1.5 ${
    athlete.isLead ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
  }`}>
    {athlete.isLead ? 'LEAD' : 'TR'}
  </span>
);

// ─── LeadAdminPage ────────────────────────────────────────────────────────────

/** Deterministic Fisher-Yates shuffle seeded by a string */
function seededShuffle<T>(arr: T[], seed: string): T[] {
  const result = [...arr];
  let s = Array.from(seed).reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) | 0, 0);
  const rand = () => { s = (Math.imul(s, 1664525) + 1013904223) | 0; return (s >>> 0) / 0x100000000; };
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

type AdminTab = 'setup' | 'athletes' | 'jury' | 'rankings' | 'timetable';

const COUNTRIES: { code: string; name: string }[] = [
  { code: 'AF', name: 'Afghanistan' }, { code: 'AL', name: 'Albania' }, { code: 'DZ', name: 'Algeria' },
  { code: 'AR', name: 'Argentina' }, { code: 'AM', name: 'Armenia' }, { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' }, { code: 'AZ', name: 'Azerbaijan' }, { code: 'BH', name: 'Bahrain' },
  { code: 'BY', name: 'Belarus' }, { code: 'BE', name: 'Belgium' }, { code: 'BA', name: 'Bosnia & Herzegovina' },
  { code: 'BR', name: 'Brazil' }, { code: 'BG', name: 'Bulgaria' }, { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' }, { code: 'CN', name: 'China' }, { code: 'CO', name: 'Colombia' },
  { code: 'HR', name: 'Croatia' }, { code: 'CY', name: 'Cyprus' }, { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' }, { code: 'EG', name: 'Egypt' }, { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' }, { code: 'FR', name: 'France' }, { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' }, { code: 'GR', name: 'Greece' }, { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' }, { code: 'IS', name: 'Iceland' }, { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' }, { code: 'IR', name: 'Iran' }, { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' }, { code: 'IL', name: 'Israel' }, { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' }, { code: 'JO', name: 'Jordan' }, { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KR', name: 'Korea' }, { code: 'KW', name: 'Kuwait' }, { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LV', name: 'Latvia' }, { code: 'LB', name: 'Lebanon' }, { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' }, { code: 'LU', name: 'Luxembourg' }, { code: 'MY', name: 'Malaysia' },
  { code: 'MT', name: 'Malta' }, { code: 'MX', name: 'Mexico' }, { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' }, { code: 'ME', name: 'Montenegro' }, { code: 'MA', name: 'Morocco' },
  { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' }, { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' }, { code: 'OM', name: 'Oman' }, { code: 'PK', name: 'Pakistan' },
  { code: 'PE', name: 'Peru' }, { code: 'PH', name: 'Philippines' }, { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' }, { code: 'QA', name: 'Qatar' }, { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' }, { code: 'SA', name: 'Saudi Arabia' }, { code: 'RS', name: 'Serbia' },
  { code: 'SG', name: 'Singapore' }, { code: 'SK', name: 'Slovakia' }, { code: 'SI', name: 'Slovenia' },
  { code: 'ZA', name: 'South Africa' }, { code: 'ES', name: 'Spain' }, { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' }, { code: 'TW', name: 'Taiwan' }, { code: 'TJ', name: 'Tajikistan' },
  { code: 'TH', name: 'Thailand' }, { code: 'TN', name: 'Tunisia' }, { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' }, { code: 'UA', name: 'Ukraine' }, { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' }, { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VE', name: 'Venezuela' }, { code: 'VN', name: 'Vietnam' },
].sort((a, b) => a.name.localeCompare(b.name));

const ROUTE_OPTIONS: { id: RouteId; label: string }[] = [
  { id: 'qual1',   label: 'Qual Route 1' },
  { id: 'qual2',   label: 'Qual Route 2' },
  { id: 'finals1', label: 'Finals Route' },
];

const LeadAdminInner: React.FC = () => {
  const { competitionId, metadata, adminSecret } = useCompetition();
  const [data,      setData]      = useState<LeadCompetitionData | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('setup');
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [dirty,     setDirty]     = useState(false);

  // Setup tab
  const [newMenClassName,   setNewMenClassName]   = useState('');
  const [newWomenClassName, setNewWomenClassName] = useState('');
  // Setup tab — drag and drop
  const [dndClassId,     setDndClassId]     = useState<string | null>(null);
  const [dndFromGroupId, setDndFromGroupId] = useState<string | null>(null);
  const [dndFromGender,  setDndFromGender]  = useState<'men' | 'women' | null>(null);
  const [dndTarget,      setDndTarget]      = useState<string | null>(null);

  // Athletes tab
  const [newFirst,   setNewFirst]   = useState('');
  const [newLast,    setNewLast]    = useState('');
  const [newGender,  setNewGender]  = useState<'male' | 'female'>('male');
  const [newClassId, setNewClassId] = useState('');
  const [newIsLead,  setNewIsLead]  = useState(false);
  const [newCountry, setNewCountry] = useState('');

  // Jury tab
  const [juryRoute, setJuryRoute] = useState<RouteId>('qual1');

  // ── Generate starting order print ────────────────────────────────────────
  const generateStartingOrder = () => {
    const compName  = metadata?.name ?? 'Paraclimbing';
    const isFinals  = juryRoute === 'finals1';
    const routeLabel = isFinals ? 'Finals' : 'Qualifications';
    const qualRankMap = new Map(allGroupRankings.flatMap(({ rows }) => rows.map(r => [r.athlete.id, r.finalRank])));

    const athleteRow = (a: LeadAthlete, i: number) => {
      const bib     = bibMap.get(a.id) ?? '';
      const country = COUNTRIES.find(c => c.code === a.country)?.name ?? a.country ?? '';
      return `<tr style="border-bottom:1px solid #e5e7eb;background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="padding:5px 8px;text-align:center;font-weight:700;font-size:12pt;color:#1e3a5f;">${i + 1}</td>
        <td style="padding:5px 8px;text-align:center;font-size:9pt;color:#6b7280;font-family:monospace;">${bib}</td>
        <td style="padding:5px 8px;font-weight:600;font-size:10pt;">${a.firstName} ${a.lastName}</td>
        <td style="padding:5px 8px;text-align:center;font-weight:700;color:#1e3a5f;font-size:10pt;">${a.classId}</td>
        <td style="padding:5px 8px;font-size:9pt;color:#374151;">${country}</td>
      </tr>`;
    };

    const colHeader = (title: string, color: string) =>
      `<thead><tr style="background:#f0f4f8;border-bottom:2px solid ${color};">
        <th style="padding:4px 8px;text-align:center;width:32px;color:#1e3a5f;font-size:9pt;">#</th>
        <th style="padding:4px 8px;text-align:center;width:36px;color:#6b7280;font-size:8pt;">Bib</th>
        <th style="padding:4px 8px;text-align:left;color:#1e3a5f;font-size:9pt;" colspan="3">${title}</th>
      </tr></thead>`;

    const sectionsHtml = jurySections.map(({ label, sectionAthletes, gender }) => {
      const shuffled = seededShuffle(sectionAthletes, competitionId + label);
      const headerColor = gender === 'women' ? '#be185d' : gender === 'men' ? '#1d4ed8' : '#1e3a5f';

      if (isFinals) {
        const ordered = sectionAthletes.filter(a => finalistIds.has(a.id))
          .sort((a, b) => (qualRankMap.get(b.id) ?? 0) - (qualRankMap.get(a.id) ?? 0));
        if (ordered.length === 0) return '';
        return `<div style="margin-bottom:24px;page-break-inside:avoid;">
          <div style="background:${headerColor};color:white;padding:5px 12px;border-radius:6px 6px 0 0;display:flex;justify-content:space-between;">
            <span style="font-weight:700;font-size:12pt;">${label}</span>
            <span style="font-size:10pt;opacity:0.8;">${ordered.length} athletes</span>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:10pt;font-family:Arial,sans-serif;">
            ${colHeader('Finals order (worst first)', headerColor)}
            <tbody>${ordered.map(athleteRow).join('')}</tbody>
          </table></div>`;
      }

      // Both qual routes side by side
      const q1 = shuffled;
      const q2 = [...shuffled].reverse();
      if (q1.length === 0) return '';
      const rowCount = q1.length;
      const bodyRows = Array.from({ length: rowCount }, (_, i) => {
        const a1 = q1[i];
        const a2 = q2[i];
        const b1 = bibMap.get(a1.id) ?? '';
        const b2 = bibMap.get(a2.id) ?? '';
        const bg = i % 2 === 0 ? '#fff' : '#f9fafb';
        return `<tr style="border-bottom:1px solid #e5e7eb;background:${bg}">
          <td style="padding:4px 6px;text-align:center;font-weight:700;font-size:11pt;color:#1e3a5f;width:28px;">${i + 1}</td>
          <td style="padding:4px 6px;text-align:center;font-size:9pt;color:#6b7280;font-family:monospace;width:32px;">${b1}</td>
          <td style="padding:4px 6px;font-weight:600;font-size:10pt;">${a1.firstName} ${a1.lastName}</td>
          <td style="padding:4px 6px;text-align:center;font-weight:700;color:#1e3a5f;font-size:10pt;width:44px;">${a1.classId}</td>
          <td style="padding:4px 6px;border-left:2px solid #d1d5db;text-align:center;font-weight:700;font-size:11pt;color:#1e3a5f;width:28px;">${i + 1}</td>
          <td style="padding:4px 6px;text-align:center;font-size:9pt;color:#6b7280;font-family:monospace;width:32px;">${b2}</td>
          <td style="padding:4px 6px;font-weight:600;font-size:10pt;">${a2.firstName} ${a2.lastName}</td>
          <td style="padding:4px 6px;text-align:center;font-weight:700;color:#1e3a5f;font-size:10pt;width:44px;">${a2.classId}</td>
        </tr>`;
      }).join('');

      return `<div style="margin-bottom:24px;page-break-inside:avoid;">
        <div style="background:${headerColor};color:white;padding:5px 12px;border-radius:6px 6px 0 0;display:flex;justify-content:space-between;">
          <span style="font-weight:700;font-size:12pt;">${label}</span>
          <span style="font-size:10pt;opacity:0.8;">${rowCount} athletes</span>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:10pt;font-family:Arial,sans-serif;">
          <thead><tr style="background:#f0f4f8;border-bottom:2px solid ${headerColor};">
            <th colspan="4" style="padding:4px 8px;text-align:left;color:#1e3a5f;font-size:9pt;border-right:2px solid #d1d5db;">Route 1</th>
            <th colspan="4" style="padding:4px 8px;text-align:left;color:#1e3a5f;font-size:9pt;">Route 2 (reversed)</th>
          </tr></thead>
          <tbody>${bodyRows}</tbody>
        </table></div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><title>Starting Order — ${compName}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:Arial,sans-serif; background:#fff; color:#111; }
  @page { size:A4 landscape; margin:10mm 8mm; }
  .no-print { padding:12px 20px; background:#1e3a5f; display:flex; align-items:center; justify-content:space-between; }
  .no-print span { color:white; font-weight:700; font-size:15px; }
  .no-print button { background:white; color:#1e3a5f; border:none; padding:8px 20px; border-radius:6px; font-weight:700; cursor:pointer; font-size:14px; }
  @media print { .no-print { display:none !important; } }
</style></head>
<body>
<div class="no-print">
  <span>${compName} — Starting Order: ${routeLabel}</span>
  <button onclick="window.print()">🖨 Print / Save as PDF</button>
</div>
<div style="padding:12px 16px;max-width:1100px;margin:0 auto;">
  <div style="text-align:center;margin-bottom:16px;border-bottom:3px solid #1e3a5f;padding-bottom:10px;">
    <div style="font-size:16pt;font-weight:900;">🧗 ${compName}</div>
    <div style="font-size:12pt;font-weight:700;margin-top:3px;color:#1e3a5f;">Starting Order — ${routeLabel}</div>
    ${metadata?.date ? `<div style="font-size:9pt;color:#555;margin-top:3px;">${metadata.date}${metadata?.location ? ' · ' + metadata.location : ''}</div>` : ''}
  </div>
  ${sectionsHtml}
  <div style="margin-top:16px;font-size:9pt;color:#999;text-align:center;">${compName} · Generated ${new Date().toLocaleDateString('en-GB')}</div>
</div>
</body></html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); win.print(); }
  };

  // ── Generate bibs ─────────────────────────────────────────────────────────
  const generateBibs = () => {
    const compName = metadata?.name ?? 'Paraclimbing';
    const bibNumbers = new Map<string, number>(athletes.map((a, i) => [a.id, i + 1]));
    const sorted = [...athletes]
      .sort((a, b) => `${a.classId}${a.lastName}`.localeCompare(`${b.classId}${b.lastName}`))
      .map(a => ({ ...a, bib: bibNumbers.get(a.id) ?? 0, countryName: COUNTRIES.find(c => c.code === a.country)?.name ?? a.country ?? '' }));

    const bibCard = (a: typeof sorted[0], extraClass = '') => `
      <div class="bib${extraClass ? ' ' + extraClass : ''}">
        <div class="bib-header">
          <span>${metadata?.date ?? ''}</span>
          <span>${compName}</span>
        </div>
        <div class="bib-center">
          <div class="bib-number">${a.bib}</div>
          <div class="bib-name">${a.lastName.toUpperCase()}</div>
        </div>
        <div class="bib-footer">
          <span>${a.countryName}</span>
          <span>${a.classId}</span>
        </div>
      </div>`;

    const pages: string[] = [];
    for (let i = 0; i < sorted.length; i += 2) {
      const top    = sorted[i];
      const bottom = sorted[i + 1] ?? null;
      const isLast = i + 2 >= sorted.length;
      pages.push(`
        <div class="a4-page${isLast ? ' last' : ''}">
          ${bibCard(top, 'bib-top')}
          <div class="cut-line"></div>
          ${bottom ? bibCard(bottom, 'bib-bottom') : '<div class="bib bib-bottom bib-empty"></div>'}
        </div>`);
    }

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Bibs — ${compName}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: white; }
  @page { size: 210mm 297mm; margin: 0; }
  .no-print { padding: 12px 20px; background: #1e3a5f; display: flex; align-items: center; justify-content: space-between; }
  .no-print span { color: white; font-family: Arial; font-weight: 700; font-size: 15px; }
  .no-print button { background: white; color: #1e3a5f; border: none; padding: 8px 20px; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 14px; font-family: Arial; }
  @media print { .no-print { display: none !important; } }
  .a4-page { width: 210mm; height: 297mm; page-break-after: always; display: flex; flex-direction: column; }
  .a4-page.last { page-break-after: avoid; }
  .cut-line { height: 1mm; background: repeating-linear-gradient(90deg,#bbb 0,#bbb 4mm,transparent 4mm,transparent 8mm); }
  .bib { width: 210mm; height: 148mm; padding: 10mm; display: flex; flex-direction: column; background: white; color: black; font-family: Arial, Helvetica, sans-serif; flex-shrink: 0; }
  .bib-empty { visibility: hidden; }
  .bib-header { display: flex; justify-content: space-between; font-size: 10pt; letter-spacing: 0.8px; text-transform: uppercase; }
  .bib-center { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .bib-number { font-size: 135pt; font-weight: 900; line-height: 1; }
  .bib-name { font-size: 44pt; font-weight: bold; margin-top: 4mm; text-align: center; letter-spacing: 1px; }
  .bib-footer { display: flex; justify-content: space-between; font-size: 13pt; font-weight: bold; }
</style>
</head>
<body>
<div class="no-print">
  <span>${compName} — Bib Numbers (${sorted.length} athletes)</span>
  <button onclick="window.print()">🖨 Print / Save as PDF</button>
</div>
${pages.join('\n')}
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); win.print(); }
  };

  // Timetable tab
  const [timetable,    setTimetable]    = useState<TimetableRow[]>([]);
  const [ttStart,      setTtStart]      = useState('');
  const [ttEnd,        setTtEnd]        = useState('');
  const [ttActivity,   setTtActivity]   = useState('');
  const [ttHighlight,  setTtHighlight]  = useState(false);
  const [ttSaving,     setTtSaving]     = useState(false);

  useEffect(() => {
    loadLeadData(competitionId)
      .then(d => {
        setData(d);
        setLoading(false);
        if (d.setup.classes.length > 0)  setNewClassId(d.setup.classes[0].id);
      })
      .catch(err => { setError(String(err)); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  // Sync timetable from competition metadata
  useEffect(() => {
    if (metadata?.timetable) setTimetable([...metadata.timetable].sort((a, b) => a.start.localeCompare(b.start)));
  }, [metadata]);

  const persist = useCallback((d: LeadCompetitionData) => {
    setData(d);
    setDirty(true);
    setSaved(false);
  }, []);

  // Auto-save: debounce 800 ms after any dirty change
  useEffect(() => {
    if (!dirty || !data) return;
    setSaving(true);
    const t = setTimeout(() => {
      saveLeadData(competitionId, data)
        .then(() => { setSaved(true); setDirty(false); setTimeout(() => setSaved(false), 2000); })
        .catch(err => setError(String(err)))
        .finally(() => setSaving(false));
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, data]);

  // ── Setup handlers ──────────────────────────────────────────────────────

  const saveTimetable = useCallback((rows: TimetableRow[]) => {
    setTtSaving(true);
    updateCompetition(competitionId, { timetable: rows })
      .catch(err => console.error('Timetable save error:', err))
      .finally(() => setTtSaving(false));
  }, [competitionId]);

  const addTimetableRow = () => {
    if (!ttActivity.trim()) return;
    const rows = [...timetable, { start: ttStart, end: ttEnd, activity: ttActivity.trim(), highlight: ttHighlight }]
      .sort((a, b) => a.start.localeCompare(b.start));
    setTimetable(rows);
    saveTimetable(rows);
    setTtStart(''); setTtEnd(''); setTtActivity(''); setTtHighlight(false);
  };

  const removeTimetableRow = (i: number) => {
    const rows = timetable.filter((_, idx) => idx !== i);
    setTimetable(rows);
    saveTimetable(rows);
  };

  const updateTimetableRow = (i: number, patch: Partial<TimetableRow>) => {
    const rows = timetable
      .map((r, idx) => idx === i ? { ...r, ...patch } : r)
      .sort((a, b) => a.start.localeCompare(b.start));
    setTimetable(rows);
    saveTimetable(rows);
  };

  // ── Auto-name helper ────────────────────────────────────────────────────
  const autoGroupName = (classIds: string[], classes: LeadClass[]) =>
    classIds.map(id => classes.find(c => c.id === id)?.name ?? id).join(' & ');

  const removeClassFromGroup = (classId: string, groupId: string) => {
    if (!data) return;
    const group = data.setup.groups.find(g => g.id === groupId);
    if (!group) return;
    const remaining = (group.classIds ?? []).filter(id => id !== classId);
    let groups: LeadGroup[];
    let athletes = data.athletes;
    if (remaining.length === 0) {
      groups = data.setup.groups.filter(g => g.id !== groupId);
      athletes = athletes.map(a => a.groupId === groupId ? { ...a, groupId: '' } : a);
    } else {
      const newName = autoGroupName(remaining, data.setup.classes);
      groups = data.setup.groups.map(g => g.id === groupId ? { ...g, classIds: remaining, name: newName } : g);
      athletes = athletes.map(a =>
        a.groupId === groupId && a.classId === classId ? { ...a, groupId: '' } : a
      );
    }
    persist({ ...data, setup: { ...data.setup, groups }, athletes });
  };

  const dropOnStandalone = (draggedClassId: string, fromGroupId: string | null, targetClassId: string, gender: 'men' | 'women') => {
    if (!data || draggedClassId === targetClassId) return;
    let groups = [...data.setup.groups];
    let athletes = [...data.athletes];
    if (fromGroupId) {
      const fromGroup = groups.find(g => g.id === fromGroupId);
      if (fromGroup) {
        const remaining = (fromGroup.classIds ?? []).filter(id => id !== draggedClassId);
        if (remaining.length === 0) {
          groups = groups.filter(g => g.id !== fromGroupId);
          athletes = athletes.map(a => a.groupId === fromGroupId ? { ...a, groupId: '' } : a);
        } else {
          const newName = autoGroupName(remaining, data.setup.classes);
          groups = groups.map(g => g.id === fromGroupId ? { ...g, classIds: remaining, name: newName } : g);
          athletes = athletes.map(a =>
            a.groupId === fromGroupId && a.classId === draggedClassId ? { ...a, groupId: '' } : a
          );
        }
      }
    }
    const newClassIds = [draggedClassId, targetClassId];
    const newName = autoGroupName(newClassIds, data.setup.classes);
    const newGroup: LeadGroup = { id: generateId(), name: newName, gender, classIds: newClassIds };
    groups = [...groups, newGroup];
    const groupGender = gender === 'men' ? 'male' : 'female';
    athletes = athletes.map(a => {
      if ((a.classId === draggedClassId || a.classId === targetClassId) && a.gender === groupGender)
        return { ...a, groupId: newGroup.id };
      return a;
    });
    persist({ ...data, setup: { ...data.setup, groups }, athletes });
  };

  const dropOnGroup = (draggedClassId: string, fromGroupId: string | null, targetGroupId: string) => {
    if (!data || fromGroupId === targetGroupId) return;
    let groups = [...data.setup.groups];
    let athletes = [...data.athletes];
    if (fromGroupId) {
      const fromGroup = groups.find(g => g.id === fromGroupId);
      if (fromGroup) {
        const remaining = (fromGroup.classIds ?? []).filter(id => id !== draggedClassId);
        if (remaining.length === 0) {
          groups = groups.filter(g => g.id !== fromGroupId);
          athletes = athletes.map(a => a.groupId === fromGroupId ? { ...a, groupId: '' } : a);
        } else {
          const newName = autoGroupName(remaining, data.setup.classes);
          groups = groups.map(g => g.id === fromGroupId ? { ...g, classIds: remaining, name: newName } : g);
          athletes = athletes.map(a =>
            a.groupId === fromGroupId && a.classId === draggedClassId ? { ...a, groupId: '' } : a
          );
        }
      }
    }
    const targetGroup = groups.find(g => g.id === targetGroupId);
    if (!targetGroup || (targetGroup.classIds ?? []).includes(draggedClassId)) return;
    const newClassIds = [...(targetGroup.classIds ?? []), draggedClassId];
    const newName = autoGroupName(newClassIds, data.setup.classes);
    groups = groups.map(g => g.id === targetGroupId ? { ...g, classIds: newClassIds, name: newName } : g);
    const groupGender = targetGroup.gender === 'men' ? 'male' : 'female';
    athletes = athletes.map(a => {
      if (a.classId === draggedClassId && a.gender === groupGender)
        return { ...a, groupId: targetGroupId };
      return a;
    });
    persist({ ...data, setup: { ...data.setup, groups }, athletes });
  };

  const addClass = (gender: 'men' | 'women', name: string) => {
    if (!data || !name.trim()) return;
    const c: LeadClass = { id: generateId(), name: name.trim(), gender };
    persist({ ...data, setup: { ...data.setup, classes: [...data.setup.classes, c] } });
    if (gender === 'men') setNewMenClassName(''); else setNewWomenClassName('');
  };

  const deleteClass = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this class? Athletes assigned to it will be unassigned.')) return;
    persist({
      ...data,
      setup: {
        ...data.setup,
        classes: data.setup.classes.filter(c => c.id !== id),
        groups: data.setup.groups.map(g => ({ ...g, classIds: (g.classIds ?? []).filter(cid => cid !== id) })),
      },
      athletes: data.athletes.map(a => a.classId === id ? { ...a, classId: '', groupId: '' } : a),
    });
  };

  const toggleLock = () => {
    if (!data) return;
    persist({ ...data, setup: { ...data.setup, locked: !data.setup.locked } });
  };

  const resetAll = () => {
    if (!window.confirm('Reset ALL lead data? This cannot be undone.')) return;
    persist({ setup: { groups: [], classes: [], locked: false }, athletes: [], scores: {} });
  };

  // ── Athletes handlers ────────────────────────────────────────────────────

  const addAthlete = () => {
    if (!data || !newFirst.trim() || !newLast.trim()) return;
    const derivedGroupId = data.setup.groups.find(g =>
      (g.classIds ?? []).includes(newClassId) &&
      (!g.gender || g.gender === (newGender === 'male' ? 'men' : 'women'))
    )?.id ?? '';
    const a: LeadAthlete = {
      id: generateId(), firstName: newFirst.trim(), lastName: newLast.trim(),
      gender: newGender, classId: newClassId, groupId: derivedGroupId, isLead: newIsLead,
      country: newCountry || undefined,
    };
    persist({ ...data, athletes: [...data.athletes, a] });
    setNewFirst(''); setNewLast('');
  };

  const updateAthlete = (id: string, patch: Partial<LeadAthlete>) => {
    if (!data) return;
    const fullPatch: Partial<LeadAthlete> = { ...patch };
    const hasAutoGroups = data.setup.groups.some(g => (g.classIds ?? []).length > 0);
    if (hasAutoGroups && ('classId' in patch || 'gender' in patch)) {
      const cur = data.athletes.find(a => a.id === id);
      const effClassId = patch.classId ?? cur?.classId ?? '';
      const effGender  = patch.gender  ?? cur?.gender;
      const groupGender = effGender === 'male' ? 'men' : 'women';
      fullPatch.groupId = data.setup.groups.find(g =>
        (g.classIds ?? []).includes(effClassId) &&
        (!g.gender || g.gender === groupGender)
      )?.id ?? '';
    }
    persist({ ...data, athletes: data.athletes.map(a => a.id === id ? { ...a, ...fullPatch } : a) });
  };

  const deleteAthlete = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this athlete and all their scores?')) return;
    const scores = { ...data.scores };
    (['qual1', 'qual2', 'finals1'] as RouteId[]).forEach(r => delete scores[`${id}_${r}`]);
    persist({ ...data, athletes: data.athletes.filter(a => a.id !== id), scores });
  };

  // ── Jury handler ─────────────────────────────────────────────────────────

  const updateScore = (
    athleteId: string,
    routeId: RouteId,
    patch: Partial<Omit<LeadScore, 'athleteId' | 'routeId'>>,
  ) => {
    if (!data) return;
    const merged = mergeScore(data.scores, athleteId, routeId, patch);
    // Update local state immediately without marking dirty — saveScoreEntry persists atomically
    setData({ ...data, scores: setScore(data.scores, athleteId, routeId, patch) });
    saveScoreEntry(competitionId, athleteId, routeId, merged).catch(err =>
      console.error('Score save error:', err),
    );
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
      Loading competition data…
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded max-w-lg">
        <strong>Failed to load data</strong><br />
        <span className="text-sm">{error ?? 'Unknown error'}</span><br />
        <button onClick={() => window.location.reload()} className="mt-3 text-sm underline">Retry</button>
      </div>
    </div>
  );

  const { setup, athletes, scores } = data;
  const locked = setup.locked;

  // Jury sections: explicit groups + ungrouped athletes split by (classId, gender)
  const jurySections: Array<{ label: string; sectionAthletes: LeadAthlete[]; gender: 'men' | 'women' | null }> = [];
  setup.groups.forEach(g => {
    const grpAthletes = athletes.filter(a => a.groupId === g.id);
    if (grpAthletes.length > 0) jurySections.push({ label: g.gender ? `${g.name} ${g.gender === 'men' ? 'Men' : 'Women'}` : g.name, sectionAthletes: grpAthletes, gender: g.gender ?? null });
  });
  const ungroupedAthletes = athletes.filter(a => !a.groupId);
  Array.from(new Set(ungroupedAthletes.map(a => a.classId))).forEach(classId => {
    (['male', 'female'] as const).forEach(gender => {
      const sub = ungroupedAthletes.filter(a => a.classId === classId && a.gender === gender);
      if (sub.length === 0) return;
      const className = setup.classes.find(c => c.id === classId)?.name ?? classId;
      jurySections.push({ label: `${className} ${gender === 'male' ? 'Men' : 'Women'}`, sectionAthletes: sub, gender: gender === 'male' ? 'men' : 'women' });
    });
  });

  jurySections.sort((a, b) => {
    const order = { women: 0, men: 1, null: 2 } as Record<string, number>;
    return (order[String(a.gender)] ?? 2) - (order[String(b.gender)] ?? 2);
  });

  // Rankings
  const allGroupRankings = computeAllGroupQualRankings(data);
  const finalsRankings   = computeFinalsRankings(data);

  // Bib numbers: order of adding athletes
  const bibMap = new Map<string, number>(
    athletes.map((a, i) => [a.id, i + 1])
  );

  // Set of athlete IDs who qualified for finals (per group, top N by qual rank)
  const finalistIds = new Set<string>(
    allGroupRankings.flatMap(({ group, rows }) => {
      const spots = group?.finalSpots ?? 3;
      return rows.filter(r => r.finalRank <= spots).map(r => r.athlete.id);
    })
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-dark text-white py-6 px-4 shadow">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">Lead — Admin</h1>
            <p className="text-gray-400 text-sm mt-1">
              {metadata?.name ?? competitionId} ·{' '}
              <a href={`/competition/${competitionId}`} target="_blank" rel="noopener noreferrer" className="text-blue-300 underline">
                Open public display ↗
              </a>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {saving && <span className="text-gray-300 text-sm">Saving…</span>}
            {!saving && saved && <span className="text-green-300 text-sm">Saved ✓</span>}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 flex gap-1">
          {(['setup', 'athletes', 'jury', 'rankings', 'timetable'] as AdminTab[]).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-semibold capitalize border-b-2 transition-colors ${
                activeTab === tab ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-dark'
              }`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ══ SETUP TAB ══ */}
        {activeTab === 'setup' && (
          <div>
            {locked && (
              <div className="mb-6 flex items-center justify-between p-4 rounded-lg border-2 bg-yellow-50 border-yellow-400">
                <div>
                  <p className="font-semibold text-dark">🔒 Setup is locked</p>
                  <p className="text-sm text-gray-500 mt-0.5">Unlock to modify merging groups, classes, or athlete assignments.</p>
                </div>
                <button onClick={toggleLock}
                  className="px-4 py-2 rounded font-semibold text-sm bg-yellow-500 hover:bg-yellow-600 text-white">
                  Unlock Setup
                </button>
              </div>
            )}

            {/* Merging Groups — Men / Women */}
            <div className="mt-8 grid md:grid-cols-2 gap-8">
              {(['men', 'women'] as const).map(col => {
                const colGroups     = setup.groups.filter(g => g.gender === col);
                const colClasses    = setup.classes.filter(c => !c.gender || c.gender === col);
                const colAssigned   = new Set(colGroups.flatMap(g => g.classIds ?? []));
                const colUnassigned = colClasses.filter(c => !colAssigned.has(c.id));
                const colClassName    = col === 'men' ? newMenClassName    : newWomenClassName;
                const setColClassName = col === 'men' ? setNewMenClassName : setNewWomenClassName;
                return (
                  <div key={col}>
                    <h2 className={`text-xl font-bold mb-2 ${col === 'men' ? 'text-blue-700' : 'text-pink-600'}`}>
                      {col === 'men' ? 'Men' : 'Women'}
                    </h2>
                    <p className="text-sm text-gray-500 mb-3">Drag one class onto another to merge them into a group.</p>

                    {/* Per-gender class management */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {colClasses.length === 0
                          ? <span className="text-xs text-gray-400 italic">No classes yet — add below.</span>
                          : colClasses.map(c => (
                              <div key={c.id} className="flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded text-xs font-semibold text-dark select-none">
                                {c.name}
                                {!locked && <button onClick={() => deleteClass(c.id)} className="text-gray-300 hover:text-red-500 ml-1">✕</button>}
                              </div>
                            ))
                        }
                      </div>
                      {!locked && (
                        <div className="flex gap-2">
                          <input type="text" value={colClassName}
                            onChange={e => setColClassName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addClass(col, colClassName)}
                            placeholder={`Add ${col === 'men' ? "men's" : "women's"} class (e.g. AL1)`}
                            className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary" />
                          <button onClick={() => addClass(col, colClassName)} disabled={!colClassName.trim()}
                            className="px-3 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40">
                            + Add
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Rows: groups + standalone classes */}
                    <div className="flex flex-col gap-2 mt-2">
                      {/* Groups */}
                      {colGroups.map(g => {
                        const isOver = dndTarget === g.id && dndFromGender === col;
                        return (
                          <div
                            key={g.id}
                            onDragOver={e => { if (dndFromGender === col && dndClassId) { e.preventDefault(); setDndTarget(g.id); } }}
                            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDndTarget(null); }}
                            onDrop={e => {
                              e.preventDefault();
                              if (dndClassId && dndFromGender === col) dropOnGroup(dndClassId, dndFromGroupId, g.id);
                              setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null);
                            }}
                            className={`flex flex-wrap items-center gap-2 px-3 py-2 rounded border-2 transition-colors ${
                              isOver ? 'border-secondary bg-blue-50' : 'border-gray-200 bg-white'
                            }`}
                          >
                            {(g.classIds ?? []).map(classId => {
                              const cls = setup.classes.find(c => c.id === classId);
                              if (!cls) return null;
                              return (
                                <div
                                  key={classId}
                                  draggable={!locked}
                                  onDragStart={() => { setDndClassId(classId); setDndFromGroupId(g.id); setDndFromGender(col); }}
                                  onDragEnd={() => { setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null); }}
                                  className={`flex items-center gap-1 px-2.5 py-1 bg-secondary/10 border border-secondary/30 text-secondary rounded text-xs font-semibold select-none transition-opacity ${
                                    locked ? 'cursor-default' : 'cursor-grab'
                                  } ${dndClassId === classId && dndFromGroupId === g.id ? 'opacity-40' : ''}`}
                                >
                                  {!locked && <span className="text-secondary/40">⠿</span>}
                                  {cls.name}
                                  {!locked && (
                                    <button onClick={() => removeClassFromGroup(classId, g.id)}
                                      className="text-secondary/40 hover:text-red-500 ml-0.5 leading-none">✕</button>
                                  )}
                                </div>
                              );
                            })}
                            <span className="text-xs text-gray-400 ml-auto">
                              {athletes.filter(a => a.groupId === g.id).length} athletes
                            </span>
                            <label className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                              Finals:
                              <input
                                type="number"
                                min={1}
                                max={99}
                                disabled={locked}
                                value={g.finalSpots ?? 3}
                                onChange={e => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!data || isNaN(v)) return;
                                  persist({ ...data, setup: { ...data.setup, groups: data.setup.groups.map(x => x.id === g.id ? { ...x, finalSpots: v } : x) } });
                                }}
                                className="w-12 text-center border border-gray-300 rounded px-1 py-0.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-secondary disabled:bg-gray-100 disabled:text-gray-400"
                              />
                            </label>
                          </div>
                        );
                      })}
                      {/* Standalone classes */}
                      {colUnassigned.map(c => {
                        const isOver = dndTarget === `standalone-${c.id}` && dndFromGender === col;
                        return (
                          <div
                            key={c.id}
                            draggable={!locked}
                            onDragStart={() => { setDndClassId(c.id); setDndFromGroupId(null); setDndFromGender(col); }}
                            onDragEnd={() => { setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null); }}
                            onDragOver={e => {
                              if (dndFromGender === col && dndClassId && dndClassId !== c.id) {
                                e.preventDefault();
                                setDndTarget(`standalone-${c.id}`);
                              }
                            }}
                            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDndTarget(null); }}
                            onDrop={e => {
                              e.preventDefault();
                              if (dndClassId && dndFromGender === col && dndClassId !== c.id)
                                dropOnStandalone(dndClassId, dndFromGroupId, c.id, col);
                              setDndClassId(null); setDndFromGroupId(null); setDndFromGender(null); setDndTarget(null);
                            }}
                            className={`flex items-center gap-2 px-3 py-2 rounded border-2 text-sm font-semibold select-none transition-colors ${
                              isOver
                                ? 'border-secondary bg-blue-50 text-secondary'
                                : locked
                                  ? 'border-gray-200 bg-white text-dark cursor-default'
                                  : 'border-gray-200 bg-white text-dark cursor-grab hover:border-secondary hover:bg-blue-50'
                            } ${dndClassId === c.id && dndFromGroupId === null ? 'opacity-40' : ''}`}
                          >
                            {!locked && <span className="text-gray-300">⠿</span>}
                            {c.name}
                            {isOver && <span className="text-xs text-secondary ml-auto font-normal">Drop to merge →</span>}
                          </div>
                        );
                      })}
                      {colClasses.length === 0 && (
                        <p className="text-sm text-gray-400 italic">Add classes above.</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ ATHLETES TAB ══ */}
        {activeTab === 'athletes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-dark">Athletes ({athletes.length})</h2>
              {athletes.length > 0 && (
                <button onClick={generateBibs}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-secondary text-white hover:bg-secondary/90 transition-colors">
                  🖨 Print Bibs
                </button>
              )}
            </div>

            {/* Add form */}
            <div className="bg-white rounded shadow p-4 mb-6">
              <h3 className="font-semibold text-dark mb-3 text-sm uppercase tracking-wide">Add Athlete</h3>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">First name</label>
                  <input type="text" value={newFirst} onChange={e => setNewFirst(e.target.value)}
                    placeholder="First name"
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary w-36" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Last name</label>
                  <input type="text" value={newLast} onChange={e => setNewLast(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addAthlete()}
                    placeholder="Last name"
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary w-36" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Gender</label>
                  <select value={newGender} onChange={e => setNewGender(e.target.value as 'male' | 'female')}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none">
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Class</label>
                  <select value={newClassId} onChange={e => setNewClassId(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none">
                    <option value="">— select —</option>
                    {setup.classes.filter(c => !c.gender || c.gender === (newGender === 'male' ? 'men' : 'women')).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1 opacity-40">
                  <label className="text-xs text-gray-500">Style</label>
                  <select value={newIsLead ? 'lead' : 'tr'} disabled
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none cursor-not-allowed bg-gray-100">
                    <option value="lead">Lead</option>
                    <option value="tr">Top Rope</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Country</label>
                  <select value={newCountry} onChange={e => setNewCountry(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none">
                    <option value="">—</option>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <button onClick={addAthlete} disabled={!newFirst.trim() || !newLast.trim()}
                  className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40">
                  + Add
                </button>
              </div>
            </div>

            {/* Athletes table */}
            <div className="bg-white rounded shadow overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                    <th className="px-3 py-2 text-left w-8">Bib</th>
                    <th className="px-3 py-2 text-left">First</th>
                    <th className="px-3 py-2 text-left">Last</th>
                    <th className="px-3 py-2 text-center w-16">♂/♀</th>
                    <th className="px-3 py-2 text-left w-24">Class</th>
                    <th className="px-3 py-2 text-left w-28">Merged Group</th>
                    <th className="px-3 py-2 text-left w-28">Country</th>
                    <th className="px-3 py-2 text-center w-20">Style</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {athletes.map((a, i) => (
                    <tr key={a.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-400 text-xs font-mono">{bibMap.get(a.id) ?? '—'}</td>
                      <td className="px-3 py-1.5">
                        <input type="text" value={a.firstName}
                          onChange={e => updateAthlete(a.id, { firstName: e.target.value })}
                          className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-secondary" />
                      </td>
                      <td className="px-3 py-1.5">
                        <input type="text" value={a.lastName}
                          onChange={e => updateAthlete(a.id, { lastName: e.target.value })}
                          className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-secondary" />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <select value={a.gender} onChange={e => updateAthlete(a.id, { gender: e.target.value as 'male' | 'female' })}
                          className="border border-gray-200 rounded px-1 py-1 text-xs focus:outline-none">
                          <option value="male">♂</option>
                          <option value="female">♀</option>
                        </select>
                      </td>
                      <td className="px-3 py-1.5">
                        <select value={a.classId} onChange={e => updateAthlete(a.id, { classId: e.target.value })}
                          className="border border-gray-200 rounded px-1 py-1 text-xs w-full focus:outline-none">
                          <option value="">—</option>
                          {setup.classes.filter(c => !c.gender || c.gender === (a.gender === 'male' ? 'men' : 'women')).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-1.5 text-xs text-secondary font-medium">
                        {setup.groups.find(g => g.id === a.groupId)?.name
                          || setup.classes.find(c => c.id === a.classId)?.name
                          || '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <select value={a.country ?? ''} onChange={e => updateAthlete(a.id, { country: e.target.value || undefined })}
                          className="border border-gray-200 rounded px-1 py-1 text-xs w-full focus:outline-none">
                          <option value="">—</option>
                          {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-1.5 text-center opacity-40">
                        <select value={a.isLead ? 'lead' : 'tr'} disabled
                          className="border border-gray-200 rounded px-1 py-1 text-xs cursor-not-allowed bg-gray-100">
                          <option value="lead">Lead</option>
                          <option value="tr">TR</option>
                        </select>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <button onClick={() => deleteAthlete(a.id)}
                          className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      </td>
                    </tr>
                  ))}
                  {athletes.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 italic">No athletes yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ JURY TAB ══ */}
        {activeTab === 'jury' && (
          <div>
            {/* Route selector + print button */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex rounded-lg overflow-hidden border border-gray-300">
                {ROUTE_OPTIONS.map(r => (
                  <button key={r.id} onClick={() => setJuryRoute(r.id)}
                    className={`px-4 py-2 text-sm font-semibold border-r last:border-r-0 transition-colors ${
                      juryRoute === r.id ? 'bg-secondary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-500">Holds: enter number → saves on blur/Enter &nbsp;|&nbsp; <strong>+</strong> = plus hold &nbsp;|&nbsp; <strong>TOP</strong> = topped &nbsp;|&nbsp; <strong>DNF</strong> = did not finish</span>
              <div className="ml-auto flex items-center gap-2">
                {athletes.length > 0 && (
                  <button onClick={generateStartingOrder}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-gray-700 text-white hover:bg-gray-800 transition-colors">
                    📋 Print / PDF Starting Order
                  </button>
                )}
                <a
                  href={`/competition/${competitionId}/lead-jury-print${adminSecret ? `?secret=${adminSecret}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-secondary text-white hover:bg-secondary/90 transition-colors no-underline"
                >
                  🖨 Print / PDF Score Sheets
                </a>
              </div>
            </div>

            {athletes.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 text-center">
                <p className="text-yellow-800 font-semibold">No athletes added yet.</p>
              </div>
            ) : (
              jurySections.map(({ label, sectionAthletes, gender }) => {
                const qualRankMap = new Map(allGroupRankings.flatMap(({ rows }) => rows.map(r => [r.athlete.id, r.finalRank])));
                const shuffled = seededShuffle(sectionAthletes, competitionId + label);
                const displayAthletes = juryRoute === 'finals1'
                  ? sectionAthletes
                      .filter(a => finalistIds.has(a.id))
                      .sort((a, b) => (qualRankMap.get(b.id) ?? 0) - (qualRankMap.get(a.id) ?? 0))
                  : juryRoute === 'qual2'
                  ? [...shuffled].reverse()
                  : shuffled; // qual1
                if (displayAthletes.length === 0) return null;
                return (
                <div key={label} className="mb-6">
                  <div className={`px-4 py-2 rounded-t-lg flex items-center justify-between text-white ${
                    gender === 'men' ? 'bg-blue-700' : gender === 'women' ? 'bg-pink-600' : 'bg-secondary'
                  }`}>
                    <h2 className="text-base font-bold">{label}</h2>
                    <span className="text-white/70 text-sm">{displayAthletes.length} athletes</span>
                  </div>
                  <div className="overflow-x-auto rounded-b shadow">
                    <table className="w-full text-sm border-collapse bg-white">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                          <th className="px-4 py-2 text-left min-w-40">Athlete</th>
                          <th className="px-4 py-2 text-left min-w-80">Score Entry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayAthletes.map(athlete => (
                          <tr key={athlete.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2 font-semibold text-dark whitespace-nowrap">
                              <span className="inline-block w-7 text-right text-xs font-mono text-gray-400 mr-2">{bibMap.get(athlete.id) ?? ''}</span>
                              {athlete.firstName} {athlete.lastName}
                              <AthleteBadge athlete={athlete} />
                              <span className="ml-1.5 text-xs text-gray-400">{athlete.classId}</span>
                            </td>
                            <td className="px-2 py-1">
                              <ScoreCell
                                score={getScore(scores, athlete.id, juryRoute)}
                                onChange={patch => updateScore(athlete.id, juryRoute, patch)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}

        {/* ══ RANKINGS TAB ══ */}
        {activeTab === 'rankings' && (
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-xs text-blue-800">
              <strong>Ranking formula:</strong> Combined = √(Rank₁ × Rank₂) — lower is better.
              Ties on a route share the average rank (e.g. 3rd & 4th tied → rank 3.5).
              Equal combined scores share the average final rank.
            </div>

            {allGroupRankings.length === 0 ? (
              <p className="text-gray-400 italic text-center py-8">No group rankings yet. Assign athletes to groups first.</p>
            ) : (
              allGroupRankings.map(({ group, groupId, rows }) => (
                <div key={groupId} className="mb-10">
                  <div className="bg-secondary text-white px-5 py-3 rounded-t-lg flex items-center justify-between">
                    <h2 className="text-lg font-bold">{group?.name ?? `Group "${groupId}"`}</h2>
                    <span className="text-blue-200 text-sm">{rows.length} athletes</span>
                  </div>
                  <div className="bg-white rounded-b-lg shadow overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
                          <th className="px-3 py-2 text-center w-12">Rank</th>
                          <th className="px-4 py-2 text-left min-w-36">Athlete</th>
                          <th className="px-4 py-2 text-center w-16 border-l border-gray-200">Q1 Score</th>
                          <th className="px-3 py-2 text-center w-14">Q1 Rank</th>
                          <th className="px-4 py-2 text-center w-16 border-l border-gray-200">Q2 Score</th>
                          <th className="px-3 py-2 text-center w-14">Q2 Rank</th>
                          <th className="px-4 py-2 text-center w-24 border-l border-gray-200">Combined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(row => {
                          const entered = (s: LeadScore | undefined) => !!s && (s.top || s.dnf || s.holds !== null);
                          const hasScore = entered(row.score1) || entered(row.score2);
                          const isFirst = row.finalRank === 1 && hasScore;
                          return (
                            <tr key={row.athlete.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isFirst ? 'bg-yellow-50' : ''}`}>
                              <td className="px-3 py-2 text-center font-bold text-secondary">{hasScore ? formatRank(row.finalRank) : '—'}</td>
                              <td className="px-4 py-2 font-semibold text-dark whitespace-nowrap">
                                {row.athlete.firstName} {row.athlete.lastName}
                                <AthleteBadge athlete={row.athlete} />
                                <span className="ml-1.5 text-xs text-gray-400">{row.athlete.classId}</span>
                              </td>
                              <td className={`px-4 py-2 text-center font-mono font-bold border-l border-gray-100 ${row.score1?.top ? 'text-green-700' : row.score1?.dnf ? 'text-red-500' : 'text-blue-700'}`}>
                                {formatLeadScore(row.score1)}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600">{entered(row.score1) ? formatRank(row.rank1) : '—'}</td>
                              <td className={`px-4 py-2 text-center font-mono font-bold border-l border-gray-100 ${row.score2?.top ? 'text-green-700' : row.score2?.dnf ? 'text-red-500' : 'text-blue-700'}`}>
                                {formatLeadScore(row.score2)}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600">{entered(row.score2) ? formatRank(row.rank2) : '—'}</td>
                              <td className="px-4 py-2 text-center font-bold text-primary border-l border-gray-100">
                                {hasScore ? formatCombined(row.combined) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}

            {/* Finals */}
            {finalsRankings.length > 0 && (
              <div className="mt-10">
                {computeFinalsRankingsByGroup(data).map(({ group, groupId, rows }) => (
                  <div key={groupId} className="mb-8">
                    <div className="bg-dark text-white px-5 py-3 rounded-t-lg flex items-center justify-between">
                      <h2 className="text-lg font-bold">{group?.name ?? `Group "${groupId}"`} — Finals</h2>
                      <span className="text-gray-300 text-sm">{rows.length} finalists</span>
                    </div>
                    <div className="bg-white rounded-b-lg shadow overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
                            <th className="px-4 py-2 text-center w-16">Rank</th>
                            <th className="px-4 py-2 text-left">Athlete</th>
                            <th className="px-4 py-2 text-center w-24">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(row => (
                            <tr key={row.athlete.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-2 text-center font-bold text-secondary text-lg">
                                {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : formatRank(row.rank)}
                              </td>
                              <td className="px-4 py-2 font-semibold text-dark">
                                {row.athlete.firstName} {row.athlete.lastName}
                                <AthleteBadge athlete={row.athlete} />
                                <span className="ml-1.5 text-xs text-gray-400">{row.athlete.classId}</span>
                              </td>
                              <td className={`px-4 py-2 text-center font-mono font-bold text-lg ${row.score?.top ? 'text-green-700' : row.score?.dnf ? 'text-red-500' : 'text-blue-700'}`}>
                                {formatLeadScore(row.score)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ TIMETABLE TAB ══ */}
        {activeTab === 'timetable' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-dark">Timetable</h2>
              {ttSaving && <span className="text-sm text-gray-400">Saving…</span>}
            </div>

            {/* Add row form */}
            <div className="bg-white rounded shadow p-4 mb-6">
              <h3 className="font-semibold text-dark mb-3 text-sm uppercase tracking-wide">Add Row</h3>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">Start</label>
                  <input type="time" value={ttStart} onChange={e => setTtStart(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary w-28" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">End</label>
                  <input type="time" value={ttEnd} onChange={e => setTtEnd(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary w-28" />
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-48">
                  <label className="text-xs text-gray-500">Activity</label>
                  <input type="text" value={ttActivity} onChange={e => setTtActivity(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addTimetableRow()}
                    placeholder="e.g. Lead Qualification"
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 pb-2 cursor-pointer">
                  <input type="checkbox" checked={ttHighlight} onChange={e => setTtHighlight(e.target.checked)} className="accent-secondary" />
                  Highlight
                </label>
                <button onClick={addTimetableRow} disabled={!ttActivity.trim()}
                  className="px-4 py-2 bg-secondary text-white rounded text-sm font-semibold hover:bg-blue-800 disabled:opacity-40 pb-2">
                  + Add
                </button>
              </div>
            </div>

            {/* Timetable rows */}
            {timetable.length === 0 ? (
              <p className="text-gray-400 italic text-sm">No rows yet — add the first one above.</p>
            ) : (
              <div className="bg-white rounded shadow overflow-hidden divide-y divide-gray-100">
                {timetable.map((row, i) => (
                  <div key={i} className={`flex items-center gap-3 px-4 py-2 ${row.highlight ? 'bg-white' : 'bg-gray-50'}`}>
                    <input type="time" value={row.start}
                      onChange={e => updateTimetableRow(i, { start: e.target.value })}
                      className="border border-gray-200 rounded px-2 py-1 text-sm font-mono w-24 focus:outline-none focus:ring-2 focus:ring-secondary" />
                    <span className="text-gray-300 text-xs">→</span>
                    <input type="time" value={row.end}
                      onChange={e => updateTimetableRow(i, { end: e.target.value })}
                      className="border border-gray-200 rounded px-2 py-1 text-sm font-mono w-24 focus:outline-none focus:ring-2 focus:ring-secondary" />
                    <input type="text" value={row.activity}
                      onChange={e => updateTimetableRow(i, { activity: e.target.value })}
                      className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-secondary" />
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer shrink-0">
                      <input type="checkbox" checked={!!row.highlight}
                        onChange={e => updateTimetableRow(i, { highlight: e.target.checked })}
                        className="accent-secondary" />
                      HL
                    </label>
                    <button onClick={() => removeTimetableRow(i)}
                      className="text-red-400 hover:text-red-600 text-xs shrink-0">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const LeadAdminPage: React.FC = () => (
  <AdminAccessGate>
    <LeadAdminInner />
  </AdminAccessGate>
);

export default LeadAdminPage;
